<script setup lang="ts">
import { computed } from 'vue';
import { useTrip } from '@/stores/trip';
import { STATIONS, STATION_INDEX } from '@/data/graph';
import { haversine } from '@/core/geo';
import { canChooseModeBetween } from '@/core/edges';
import { fmtTime, fmtDate, fmtHour } from '@/core/format';
import IconButton from '@/components/ui/IconButton.vue';

/**
 * Liste des étapes du voyage.
 * Pour chaque étape :
 *   - Numéro, nom de la ville
 *   - Contrôles : monter / descendre / supprimer
 *   - Compteur de nuits sur place
 *   - Heure de départ par étape (override de l'heure globale)
 *   - Choix train/bus par étape (override de mode, si choix disponible)
 *   - Bandeau dates si une date de départ est définie
 *   - Encart "segment vers la suivante" (mode, durée, correspondances)
 *
 * Tout dérive de `trip.calendar` qui est un `computed` du store : pas de duplication d'état.
 */
const trip = useTrip();

const calendar = computed(() => trip.calendar.value);

interface SegmentDescriptor {
  modeLabel: string;
  modeClass: 'train' | 'bus' | 'night' | 'mixed' | 'none';
  modeIcon: string;
  detail: string;
  totalTime: string;
  hasNightMixed: boolean;
  km: number;
  unreachable: boolean;
}

function describeSegmentToNext(index: number): SegmentDescriptor | null {
  const stops = trip.state.routeStops;
  if (index >= stops.length - 1) return null;
  const s = STATIONS[STATION_INDEX[stops[index].id]];
  const nextS = STATIONS[STATION_INDEX[stops[index + 1].id]];
  const km = haversine(s.coords, nextS.coords);
  const path = calendar.value.cal[index]?.segmentPath;

  if (!path) {
    const effectiveMode = stops[index].modeOverride ?? trip.state.routeMode;
    const modeLabelByMode: Record<string, string> = {
      train: 'train',
      bus: 'bus',
      night: 'train (de nuit prioritaire)',
      best: 'train ni bus',
    };
    return {
      modeLabel: 'Non desservi',
      modeClass: 'none',
      modeIcon: 'ti-alert-triangle',
      detail: `${km} km — aucune liaison en ${modeLabelByMode[effectiveMode]}`,
      totalTime: '',
      hasNightMixed: false,
      km,
      unreachable: true,
    };
  }

  const firstMode = path.segments[0].mode;
  const allSame = path.segments.every((seg) => seg.mode === firstMode);
  // Seuil 6h cohérent avec NIGHT_SEGMENT_MIN_DURATION dans constants.ts :
  // un train de nuit < 6h n'apporte pas l'économie d'une nuit d'hôtel.
  const hasNight = path.segments.some(
    (seg) => seg.mode === 'train' && seg.night && seg.cost >= 360,
  );
  const allNight = path.segments.every(
    (seg) => seg.mode === 'train' && seg.night,
  );
  // Bus de nuit : utile pour le label informatif. Tous les segments en bus,
  // et au moins un explicitement marqué bus de nuit.
  const allBus = path.segments.every((seg) => seg.mode === 'bus');
  const hasNightBus = path.segments.some(
    (seg) => seg.mode === 'bus' && seg.nightBus,
  );

  let modeLabel: string;
  let modeClass: SegmentDescriptor['modeClass'];
  let modeIcon: string;
  if (allNight && hasNight) {
    modeLabel = 'train de nuit';
    modeClass = 'night';
    modeIcon = 'ti-moon';
  } else if (allBus && hasNightBus) {
    // Bus mais avec au moins un segment de nuit identifié — on garde la classe
    // "bus" (couleur vert), juste le label précise "de nuit" et l'icône change.
    modeLabel = 'bus de nuit';
    modeClass = 'bus';
    modeIcon = 'ti-moon';
  } else if (allSame) {
    modeLabel = firstMode;
    modeClass = firstMode;
    modeIcon = firstMode === 'train' ? 'ti-train' : 'ti-bus';
  } else {
    modeLabel = 'mixte';
    modeClass = 'mixed';
    modeIcon = 'ti-route';
  }

  const detail =
    path.segments.length === 1
      ? `direct · ${km} km`
      : `via ${path.segments
          .slice(0, -1)
          .map((seg) => STATIONS[STATION_INDEX[seg.to]].name)
          .join(' → ')} · ${path.segments.length - 1} corresp.`;

  return {
    modeLabel,
    modeClass,
    modeIcon,
    detail,
    totalTime: fmtTime(path.total),
    hasNightMixed: hasNight && !allNight,
    km,
    unreachable: false,
  };
}

/**
 * Indique si l'override train/bus a du sens pour le segment partant de l'étape `i`.
 * → vrai uniquement si l'arête directe entre i et i+1 dispose des deux modes.
 *   Sinon on masque le contrôle (pas la peine d'offrir un choix vide).
 */
function canOverrideModeAt(i: number): boolean {
  const stops = trip.state.routeStops;
  if (i >= stops.length - 1) return false;
  return canChooseModeBetween(stops[i].id, stops[i + 1].id);
}

const stopsWithMeta = computed(() => {
  return trip.state.routeStops.map((stop, i) => {
    const s = STATIONS[STATION_INDEX[stop.id]];
    return {
      stop,
      station: s,
      index: i,
      isFirst: i === 0,
      isLast: i === trip.state.routeStops.length - 1,
      calItem: calendar.value.cal[i],
      segment: describeSegmentToNext(i),
      canOverrideMode: canOverrideModeAt(i),
    };
  });
});

const modeBadgeClass = {
  train: 'bg-mode-train/10 text-mode-train',
  bus: 'bg-mode-bus/10 text-mode-bus',
  night: 'bg-mode-night/10 text-mode-night',
  mixed: 'bg-accent/10 text-accent',
  none: 'bg-red-50 text-red-700',
};

/**
 * Heure affichée dans l'input.
 * Si l'étape a un override → on l'affiche.
 * Sinon → on affiche l'heure globale (hérité), sans pour autant la matérialiser
 * dans le state (cela reste un héritage).
 */
function timeForStop(stopIndex: number): string {
  const s = trip.state.routeStops[stopIndex];
  return s?.startTime || trip.state.tripStartTime || '09:00';
}

function onTimeChange(stopIndex: number, e: Event): void {
  const value = (e.target as HTMLInputElement).value;
  if (!value) {
    trip.setStopStartTime(stopIndex, null);
    return;
  }
  trip.setStopStartTime(stopIndex, value);
}

function resetTime(stopIndex: number): void {
  trip.setStopStartTime(stopIndex, null);
}

function setModeOverride(stopIndex: number, mode: 'train' | 'bus' | null): void {
  trip.setStopModeOverride(stopIndex, mode);
}
</script>

<template>
  <!-- Empty state -->
  <div
    v-if="trip.state.routeStops.length === 0"
    class="flex flex-col items-center text-center py-8 px-4 bg-surface-muted/40 rounded-md"
  >
    <i class="ti ti-map-pin text-3xl text-text-faint mb-2" aria-hidden="true"></i>
    <p class="font-medium">Aucune étape pour le moment</p>
    <span class="text-sm text-text-muted mt-1">
      Cliquez sur une ville de la carte puis sur « Ajouter à mon itinéraire »,
      ou utilisez le champ ci-dessus.
    </span>
  </div>

  <!-- Stops list -->
  <ol v-else class="space-y-3">
    <li
      v-for="item in stopsWithMeta"
      :key="item.index"
      class="space-y-2"
    >
      <!-- Stop card -->
      <div class="p-3 bg-surface border border-DEFAULT rounded-md">
        <div class="flex items-start gap-3">
          <div
            class="w-6 h-6 flex-shrink-0 rounded-full bg-accent text-white text-xs font-semibold flex items-center justify-center"
          >
            {{ item.index + 1 }}
          </div>
          <div class="flex-1 font-medium">
            {{ item.station.name }}
          </div>
          <div class="flex gap-1">
            <IconButton
              icon="ti-chevron-up"
              size="sm"
              ariaLabel="Monter"
              :disabled="item.isFirst"
              @click="trip.moveStop(item.index, 'up')"
            />
            <IconButton
              icon="ti-chevron-down"
              size="sm"
              ariaLabel="Descendre"
              :disabled="item.isLast"
              @click="trip.moveStop(item.index, 'down')"
            />
            <IconButton
              icon="ti-x"
              size="sm"
              variant="danger"
              ariaLabel="Supprimer"
              @click="trip.removeStop(item.index)"
            />
          </div>
        </div>

        <!-- Nights -->
        <div class="flex items-center justify-between mt-3 pt-3 border-t border-DEFAULT">
          <span class="inline-flex items-center gap-1.5 text-sm text-text-muted">
            <i class="ti ti-bed" aria-hidden="true"></i> Séjour
          </span>
          <div class="flex items-center gap-2">
            <IconButton
              icon="ti-minus"
              size="sm"
              variant="ghost"
              ariaLabel="Moins de nuits"
              :disabled="(item.stop.nights || 0) === 0"
              @click="trip.setNights(item.index, (item.stop.nights || 0) - 1)"
            />
            <span class="text-sm font-medium min-w-[60px] text-center">
              {{ item.stop.nights || 0 }}
              {{ (item.stop.nights || 0) <= 1 ? 'nuit' : 'nuits' }}
            </span>
            <IconButton
              icon="ti-plus"
              size="sm"
              variant="ghost"
              ariaLabel="Plus de nuits"
              @click="trip.setNights(item.index, (item.stop.nights || 0) + 1)"
            />
          </div>
        </div>

        <!-- Heure de départ (uniquement pour les étapes qui en ont un : pas la dernière) -->
        <div
          v-if="!item.isLast"
          class="flex items-center justify-between mt-3 pt-3 border-t border-DEFAULT"
        >
          <span class="inline-flex items-center gap-1.5 text-sm text-text-muted">
            <i class="ti ti-clock" aria-hidden="true"></i>
            Heure de départ
          </span>
          <div class="flex items-center gap-1.5">
            <input
              type="time"
              :value="timeForStop(item.index)"
              :class="[
                'w-24 px-2 py-1 bg-surface border rounded-md text-sm outline-none focus:border-accent transition-colors duration-160',
                item.stop.startTime ? 'border-accent text-text font-medium' : 'border-DEFAULT text-text-muted',
              ]"
              :title="item.stop.startTime ? 'Heure personnalisée' : `Hérité de l'heure globale (${trip.state.tripStartTime})`"
              @change="onTimeChange(item.index, $event)"
            />
            <IconButton
              v-if="item.stop.startTime"
              icon="ti-rotate"
              size="sm"
              variant="ghost"
              ariaLabel="Réinitialiser à l'heure globale"
              title="Réinitialiser à l'heure globale"
              @click="resetTime(item.index)"
            />
          </div>
        </div>

        <!-- Choix train/bus (uniquement quand l'arête directe propose les deux) -->
        <div
          v-if="!item.isLast && item.canOverrideMode"
          class="flex items-center justify-between mt-3 pt-3 border-t border-DEFAULT"
        >
          <span class="inline-flex items-center gap-1.5 text-sm text-text-muted">
            <i class="ti ti-route" aria-hidden="true"></i>
            Transport
          </span>
          <div class="inline-flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
            <button
              type="button"
              :class="[
                'inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors duration-160',
                !item.stop.modeOverride
                  ? 'bg-surface text-text shadow-sm'
                  : 'text-text-muted hover:text-text',
              ]"
              :title="`Hériter du mode global (${trip.state.routeMode})`"
              @click="setModeOverride(item.index, null)"
            >
              <i class="ti ti-arrow-back-up" aria-hidden="true"></i>
              Auto
            </button>
            <button
              type="button"
              :class="[
                'inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors duration-160',
                item.stop.modeOverride === 'train'
                  ? 'bg-surface text-mode-train shadow-sm'
                  : 'text-text-muted hover:text-text',
              ]"
              @click="setModeOverride(item.index, 'train')"
            >
              <i class="ti ti-train" aria-hidden="true"></i>
              Train
            </button>
            <button
              type="button"
              :class="[
                'inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors duration-160',
                item.stop.modeOverride === 'bus'
                  ? 'bg-surface text-mode-bus shadow-sm'
                  : 'text-text-muted hover:text-text',
              ]"
              @click="setModeOverride(item.index, 'bus')"
            >
              <i class="ti ti-bus" aria-hidden="true"></i>
              Bus
            </button>
          </div>
        </div>

        <!-- Dates -->
        <div
          v-if="calendar.hasDate && (item.calItem.arrive || item.calItem.depart)"
          class="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-DEFAULT text-xs"
        >
          <span
            v-if="item.isFirst && item.calItem.depart"
            class="inline-flex items-center gap-1.5 px-2 py-1 bg-accent/10 text-accent rounded-md"
          >
            <i class="ti ti-flag" aria-hidden="true"></i>
            {{ fmtDate(item.calItem.depart) }} · {{ fmtHour(item.calItem.depart) }}
          </span>
          <template v-else-if="item.calItem.arrive">
            <span
              class="inline-flex items-center gap-1.5 px-2 py-1 bg-surface-muted text-text rounded-md"
            >
              <i class="ti ti-flag-2" aria-hidden="true"></i>
              Arrivée {{ fmtDate(item.calItem.arrive) }} · {{ fmtHour(item.calItem.arrive) }}
            </span>
            <template v-if="!item.isLast && item.calItem.depart">
              <span class="text-text-faint">→</span>
              <span
                class="inline-flex items-center gap-1.5 px-2 py-1 bg-accent/10 text-accent rounded-md"
              >
                <i class="ti ti-arrow-up-right" aria-hidden="true"></i>
                Départ {{ fmtDate(item.calItem.depart) }} · {{ fmtHour(item.calItem.depart) }}
              </span>
            </template>
          </template>
        </div>
      </div>

      <!-- Segment vers le suivant -->
      <div
        v-if="item.segment"
        :class="[
          'flex flex-wrap items-center gap-2 px-3 py-2 text-xs rounded-md ml-3',
          item.segment.unreachable ? 'bg-red-50' : 'bg-surface-muted/60',
        ]"
      >
        <span
          :class="[
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium',
            modeBadgeClass[item.segment.modeClass],
          ]"
        >
          <i :class="['ti', item.segment.modeIcon]" aria-hidden="true"></i>
          {{ item.segment.modeLabel }}
        </span>
        <span v-if="item.segment.totalTime" class="font-mono font-medium">{{
          item.segment.totalTime
        }}</span>
        <span class="text-text-muted">{{ item.segment.detail }}</span>
        <span
          v-if="item.segment.hasNightMixed"
          class="inline-flex items-center gap-1 text-mode-night"
        >
          <i class="ti ti-moon" aria-hidden="true"></i> nuit en train
        </span>
      </div>
    </li>
  </ol>
</template>
